import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  Close,
} from '@mui/icons-material';
import {
  User,
  UserFilterParams,
  UpdateUserRequest,
  RowData,
  TableColumn,
} from '../types';
import apiService from '../services/api';
import UserEditDialog from '../components/modals/UserEditDialog';
import UserViewDialog from '../components/modals/UserViewDialog';
import UserRoleDialog from '../components/modals/UserRoleDialog';
import PermissionGuard from '../components/PermissionGuard';
import { Permissions, hasPermission } from '../utils/permissions';
import { useAuth } from '../context/useAuth';
import { AvatarWrapper, DataTableWrapper } from '../styles/globalStyle';
import DataTable from '../components/dataTable';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState<UserFilterParams>({
    page: 1,
    limit: 25,
    searchTerm: '',
    isActive: undefined,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [roleUser, setRoleUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiService.getUsers(filters);
      console.log('response', response);
      setUsers(response.data || []);
      setTotal(response.total || 0);
      // console.log(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setViewDialogOpen(true);
    setActionMenuAnchor(null);
  };

  // const handleEditUser = (user: User) => {
  //   setEditingUser(user);
  //   setEditDialogOpen(true);
  //   setActionMenuAnchor(null);
  // };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await apiService.toggleUserStatus(user.id);
      fetchUsers();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle user status');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.exportUsers(filters);
      const data = response.data || [];

      // Convert to CSV
      const csvContent = [
        Object.keys(data[0] || {}).join(','),
        ...data.map(item => Object.values(item).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export users');
    }
  };

  const handleUserSaved = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleFilterChange = (field: keyof UserFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filtering
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleManageRole = (user: User) => {
    setRoleUser(user);
    setRoleDialogOpen(true);
    closeActionMenu();
  };

  const handleRoleUpdated = () => {
    fetchUsers();
    setRoleDialogOpen(false);
    setRoleUser(null);
  };

  const getUserTypeColor = (userType: string | undefined) => {
    switch (userType) {
      case 'Admin':
        return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'Staff':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'Exhibitor':
        return { bg: '#dcfce7', color: '#16a34a' };
      case 'Delegate':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'Speaker':
        return { bg: '#f3e8ff', color: '#7c3aed' };
      case 'Visitor':
        return { bg: '#e0e7ff', color: '#3730a3' };
      default:
        return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? { bg: '#dcfce7', color: '#16a34a' }
      : { bg: '#fee2e2', color: '#dc2626' };
  };

  // if (loading && users.length === 0) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '50vh',
  //       }}
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const columns: TableColumn<User>[] = [
    {
      label: 'Name',
      keys: ['firstName', 'lastName', 'profileImageUrl'],
      renderGroup: values => {
        const [firstName, lastName, profileImageUrl] = values as [
          string,
          string,
          string,
        ];
        const getInitials = (firstName: string, lastName: string) => {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        };
        return (
          <AvatarWrapper>
            <Avatar className="avatar" src={profileImageUrl}>
              {getInitials(firstName, lastName)}
            </Avatar>
            <Box className="full_name">
              {firstName} {lastName}
            </Box>
          </AvatarWrapper>
        );
      },
    },
    // { label: 'Type', key: 'userType.typeName', chip: 'type' },
    { label: 'Email', key: 'email' },
    // { label: 'Role', key: 'role.name' },
    { label: 'Status', key: 'isActive', chip: 'status' },
    { label: 'Registered', key: 'createdAt' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Admin Users Management
        </Typography>
        <PermissionGuard permission={Permissions.USER_CREATE}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              py: 1,
            }}
            onClick={() => setEditDialogOpen(true)}
          >
            Add New User
          </Button>
        </PermissionGuard>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
            >
              <TextField
                placeholder="Search users..."
                value={filters.searchTerm}
                onChange={e => handleFilterChange('searchTerm', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PermissionGuard permission={Permissions.USER_EXPORT}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  sx={{ textTransform: 'none' }}
                >
                  Export
                </Button>
              </PermissionGuard>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<User>
          columns={columns}
          data={users}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewUser(row),
            },
            // {
            //   icon: <EditIcon fontSize="small" />,
            //   onClick: () => handleEditUser(row),
            // },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />

        {/* Pagination */}
        {users && users.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        <MenuItem onClick={() => selectedUser && handleViewUser(selectedUser)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {/* {hasPermission(currentUser, Permissions.USER_UPDATE) && (
          <MenuItem
            onClick={() => selectedUser && handleEditUser(selectedUser)}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )} */}
        {hasPermission(currentUser, Permissions.USER_MANAGE) && (
          <MenuItem
            onClick={() => selectedUser && handleManageRole(selectedUser)}
          >
            <ListItemIcon>
              <SecurityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Manage Role</ListItemText>
          </MenuItem>
        )}
        {hasPermission(currentUser, Permissions.USER_UPDATE) && (
          <MenuItem
            onClick={() => selectedUser && handleToggleStatus(selectedUser)}
          >
            <ListItemIcon>
              {selectedUser?.isActive ? (
                <ToggleOffIcon fontSize="small" />
              ) : (
                <ToggleOnIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
            </ListItemText>
          </MenuItem>
        )}
        {hasPermission(currentUser, Permissions.USER_DELETE) && (
          <MenuItem
            onClick={() => selectedUser && handleDeleteUser(selectedUser)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* View Dialog */}
      <UserViewDialog
        open={viewDialogOpen}
        user={viewingUser}
        onClose={() => {
          setViewDialogOpen(false);
          setViewingUser(null);
        }}
      />

      {/* Edit Dialog */}
      {editDialogOpen && (
        <UserEditDialog
          open={editDialogOpen}
          // user={editingUser}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          onSave={handleUserSaved}
        />
      )}

      {/* Role Management Dialog */}
      <UserRoleDialog
        open={roleDialogOpen}
        user={roleUser}
        onClose={() => {
          setRoleDialogOpen(false);
          setRoleUser(null);
        }}
        onSuccess={handleRoleUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{userToDelete?.firstName}{' '}
            {userToDelete?.lastName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
